import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import RequestAmountFrom from 'components/calculator/Amount/RequestAmountFrom';
import Form from 'components/form-multi/Form';
import Loading from 'components/utils/Loading';

import { updateCalculatorFieldValue } from 'actions/calculatorAction';
import { changeFormFieldValue, setNewForm } from 'actions/formAction';

import { CalculatorLandingService } from 'services/calculatorLandingService';

import { AMOUNT_FROM, AMOUNT_TO } from 'constants/fieldsName';

import { useApp, useCalculator, useExtra, useFields, usePageMessages } from 'hooks/selectors';
import colors from 'theme/colors';
import promoMedia from 'theme/promoMedia';

import Amount from './Amount/Amount';
import RequestAmountTo from './Amount/RequestAmountTo';
import FeeBlock from './FeeBlock/FeeBlock';
import PromoTextPopup from './PromoTextPopup';
import { PromoButton } from '../no-auth/common/promo-button/PromoButton';
import PromoPopup from '../no-auth/popup/PromoPopup';

const getFormPropName = (isSend) => (isSend ? 'paymentForm' : 'requestForm');

function useDebounce(value, deps) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [value, ...deps]);

    return debouncedValue;
}

function useCalc(isSpinner, callbackCalcFunc) {
    const [state, setState] = useState({});

    const flag = useDebounce(state.value, [state.value, state.isFrom]);

    useEffect(() => {
        if (state.value !== undefined && state.isFrom !== undefined && flag !== undefined && isSpinner !== true) {
            callbackCalcFunc(state.isFrom);
        }
    }, [flag, state.isFrom]);

    return (isFrom, value) => setState({ isFrom, value });
}

export const CalculatorBlock = styled.div`
    background-color: ${colors.white};
    border: 1px solid ${colors.greyRgba28};
    box-shadow: 0 0 12px 2px ${colors.lightGrayShadow};
    padding: 32px 16px 16px 16px;
    border-radius: 16px;
    margin: 0 -16px;

    ${promoMedia.tablet744} {
        padding: 48px 32px 24px 32px;
        margin: 0 -32px;
        border-radius: 36px;
    }

    ${promoMedia.desktop1440} {
        padding: 72px 40px 48px 40px;
        border-radius: 24px;
        margin: 0;
    }
`;
export const CalculatorRow = styled.div`
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 32px;

        ${promoMedia.desktop1440} {
            margin-bottom: 48px;
        }
    }
`;
export const ButtonContainer = styled.div`
    margin-top: 40px;
    text-align: center;
    position: static;
    height: auto;
    max-width: none;
    padding-left: 0;
    padding-right: 0;
`;

const CalculatorForLanding = ({ formName, isSend, localeDependentURI, bntCaption, dataTestid, href, ...props }) => {
    const popupRef = useRef(null);
    const fields = useFields(formName);
    const { tariffErrorText, gotIt } = usePageMessages();
    const { isShowSpinner } = useApp();
    const { hasPromoForFirstPayment } = useExtra();

    const { countryMenu = {}, commission, countryTo: countryToList, countryFrom: countryFromList, ...calculator } = useCalculator();

    const helpForm = calculator[getFormPropName(isSend)] ?? {};
    const formRef = useRef();
    const [oldFeeText, setOldFeeText] = useState('');

    const calculatorLandingService = new CalculatorLandingService(
        isSend,
        formName,
        commission,
        helpForm,
        () => popupRef?.current?.open(<PromoTextPopup popupRef={popupRef} withoutBorder text={tariffErrorText} buttonText={gotIt} />),
        props.updateCalculatorFieldValue,
        props.changeFormFieldValue,
        { defaultFeeText: oldFeeText },
    );

    const [isChangingFrom, setChangingFrom] = useState(false);
    const [isChangingTo, setChangingTo] = useState(false);
    const [fee, setFee] = useState(commission?.fee);

    const calcFunc = useCalc(isShowSpinner, (isFrom) => {
        const value = fields[isFrom ? AMOUNT_FROM : AMOUNT_TO]?.value;
        const currentVal = value ? value.toString().replace(/\s/g, '') : '';
        calculatorLandingService.calcCommission(isFrom, setFee, currentVal, hasPromoForFirstPayment);
    });

    useEffect(() => {
        setOldFeeText(helpForm?.feeText);
    }, [isSend, helpForm?.feeText]);

    useEffect(() => {
        const fromAmount = fields[helpForm?.fromAmount?.name]?.value;
        stopCalculate(true);
        calcFunc(true, fromAmount);
    }, [helpForm?.fromAmount]);

    useEffect(() => {
        setFee(commission?.fee);
    }, [commission?.fee]);

    if (!helpForm) {
        return <Loading />;
    }

    const amountFromChangeHandler = (value) => {
        if (!isShowSpinner && !isChangingTo) {
            calcFunc(true, value);
        }
    };

    const amountToChangeHandler = (value) => {
        if (!isShowSpinner && !isChangingFrom) {
            calcFunc(false, value);
        }
    };

    const amountToChangeHandlerRequest = (value) => amountToChangeHandler(value);

    const { countryTo = {}, countryFrom = {} } = countryMenu;

    const startCalculateFrom = () => {
        setChangingTo(false);
        setChangingFrom(true);
    };

    const startCalculateTo = () => {
        setChangingFrom(false);
        setChangingTo(true);
    };

    const stopCalculate = (value = false) => {
        setChangingFrom(value);
        setChangingTo(value);
    };

    const renderSend = () => (
        <>
            <CalculatorRow>
                <Amount
                    onFocusHandler={startCalculateFrom}
                    onBlurHandler={stopCalculate}
                    isFrom={true}
                    isSend={isSend}
                    isLanding={true}
                    formName={formName}
                    amountFieldBean={helpForm?.fromAmount}
                    localeDependentURI={localeDependentURI}
                    countryBean={countryFrom}
                    countryTo={countryTo}
                    countryFrom={countryFrom}
                    enabledValidateMinMaxAmount={false}
                    countryList={countryFromList}
                    onChangeHandler={amountFromChangeHandler}
                />
            </CalculatorRow>
            <CalculatorRow>
                <Amount
                    onFocusHandler={startCalculateTo}
                    onBlurHandler={stopCalculate}
                    isFrom={false}
                    isSend={isSend}
                    isLanding={true}
                    localeDependentURI={localeDependentURI}
                    formName={formName}
                    amountFieldBean={helpForm?.toAmount}
                    countryBean={countryTo}
                    countryTo={countryTo}
                    countryFrom={countryFrom}
                    enabledValidateMinMaxAmount={false}
                    countryList={countryToList}
                    onChangeHandler={amountToChangeHandler}
                />
            </CalculatorRow>
        </>
    );

    const renderRequest = () => (
        <>
            <CalculatorRow>
                <RequestAmountTo
                    isFrom={false}
                    isSend={isSend}
                    isLanding={true}
                    localeDependentURI={localeDependentURI}
                    formName={formName}
                    amountFieldBean={helpForm?.toAmount}
                    countryBean={countryTo}
                    countryTo={countryTo}
                    countryFrom={countryFrom}
                    enabledValidateMinMaxAmount={false}
                    countryList={countryToList}
                    onChangeHandler={amountToChangeHandlerRequest}
                />
            </CalculatorRow>
            <CalculatorRow>
                <RequestAmountFrom
                    formName={formName}
                    localeDependentURI={localeDependentURI}
                    isLanding={true}
                    countryTo={countryTo}
                    countryBean={countryFrom}
                    countryList={countryFromList}
                />
            </CalculatorRow>
        </>
    );

    return (
        <>
            <CalculatorBlock>
                <Form id="precal_payment_form_id" formName={formName} forwardedRef={formRef}>
                    {isSend ? renderSend() : renderRequest()}
                </Form>
                <FeeBlock
                    paymentForm={helpForm}
                    feeAmount={fee}
                    fromIso={commission?.restrictionFrom?.ccyIso}
                    formName={formName}
                    isLandingRequest={!isSend}
                    countryToId={countryTo.id}
                    countryFrom={countryFrom}
                    fields={fields}
                    amountFieldBean={helpForm?.fromAmount}
                    isLanding={true}
                />
                <ButtonContainer>
                    <PromoButton caption={bntCaption} type="link" href={href} onClick={props.onClick} data-testid={dataTestid} />
                </ButtonContainer>
            </CalculatorBlock>
            <PromoPopup ref={popupRef} />
        </>
    );
};

export default connect(null, {
    setNewForm,
    changeFormFieldValue,
    updateCalculatorFieldValue,
})(CalculatorForLanding);
